import {
  ADMIN_REQUEST_PERMISSION_MODULES,
  ADMIN_ROLE_DETAIL_NESTED_VIEW_MODULES,
} from "./adminPermissionRegistry"

/**
 * Admin route → required permission rules.
 * First matching rule wins. `null` module = always allowed for admin users.
 */

/**
 * @typedef {{ module: string|null, action: string, match?: RegExp, params?: Record<string, string> }} AdminRoutePermissionRule
 */

/** @type {AdminRoutePermissionRule[]} */
export const ADMIN_ROUTE_PERMISSION_RULES = [
  { match: /^\/admin\/?$/, module: null, action: "view" },
  { match: /^\/admin\/dashboard/, module: null, action: "view" },
  { match: /^\/admin\/settings/, module: null, action: "view" },
  { match: /^\/admin\/notifications/, module: null, action: "view" },

  {
    match: /^\/admin\/requests/,
    module: "invites",
    action: "view",
    anyOf: ADMIN_REQUEST_PERMISSION_MODULES,
  },

  { match: /^\/admin\/cities\/create/, module: "cities", action: "create" },
  { match: /^\/admin\/cities\/[^/]+\/edit/, module: "cities", action: "edit" },
  {
    match: /^\/admin\/cities\/[^/]+\/members\/[^/]+\/edit/,
    module: "members",
    action: "edit",
  },
  {
    match: /^\/admin\/cities\/[^/]+\/members/,
    module: "members",
    action: "view",
  },
  {
    match: /^\/admin\/cities\/[^/]+\/projects/,
    module: "projects",
    action: "view",
  },
  {
    match: /^\/admin\/cities\/[^/]+\/reviews/,
    module: "reviews",
    action: "view",
  },
  { match: /^\/admin\/cities\/[^/]+\/calls/, module: "calls", action: "view" },
  {
    match: /^\/admin\/cities\/[^/]+\/photography/,
    module: "photography",
    action: "view",
  },
  { match: /^\/admin\/cities\/[^/]+\/flags/, module: "flags", action: "view" },
  {
    match: /^\/admin\/cities\/[^/]+\/rewards/,
    module: "rewards",
    action: "view",
  },
  {
    match: /^\/admin\/cities\/[^/]+\/shadow-working-events/,
    module: "sessions",
    action: "view",
  },
  {
    match: /^\/admin\/cities\/[^/]+\/transfers/,
    module: "city-update-requests",
    action: "view",
  },
  { match: /^\/admin\/cities/, module: "cities", action: "view" },

  { match: /^\/admin\/projects\/create/, module: "projects", action: "create" },
  {
    match: /^\/admin\/projects\/[^/]+\/edit/,
    module: "projects",
    action: "edit",
  },
  { match: /^\/admin\/projects/, module: "projects", action: "view" },

  { match: /^\/admin\/sessions/, module: "sessions", action: "view" },

  {
    match: /^\/admin\/members\/[^/]+\/edit/,
    module: "members",
    action: "edit",
  },
  { match: /^\/admin\/members/, module: "members", action: "view" },

  {
    match: /^\/admin\/rewards/,
    module: "rewards",
    action: "view",
    anyOf: ["rewards", "reward-types", "reward-claims"],
  },

  { match: /^\/admin\/inbox\/[^/]+/, module: "calls", action: "view" },
  { match: /^\/admin\/inbox/, module: "calls", action: "view" },

  { match: /^\/admin\/activity-log/, module: "activity-logs", action: "view" },
  { match: /^\/admin\/photography/, module: "photography", action: "view" },
  { match: /^\/admin\/blogs/, module: "blogs", action: "view" },

  {
    match: /^\/admin\/arts-genome\/[^/]+\/create/,
    module: "spellbooks",
    action: "create",
  },
  {
    match: /^\/admin\/arts-genome\/[^/]+\/[^/]+\/edit/,
    module: "spellbooks",
    action: "edit",
  },
  {
    match: /^\/admin\/arts-genome/,
    module: "spellbooks",
    action: "view",
    anyOf: ["spellbooks", "role-types", "role-classes"],
  },

  { match: /^\/admin\/flags/, module: "flags", action: "view" },

  {
    match: /^\/admin\/staff\/[^/]+/,
    module: "staff",
    action: "view",
  },
  { match: /^\/admin\/staff/, module: "staff", action: "view" },

  { match: /^\/admin\/roles\/create/, module: "roles", action: "create" },
  { match: /^\/admin\/roles\/[^/]+\/edit/, module: "roles", action: "edit" },
  {
    match: /^\/admin\/roles\/[^/]+/,
    module: "roles",
    action: "view",
    allOf: ADMIN_ROLE_DETAIL_NESTED_VIEW_MODULES,
  },
  {
    match: /^\/admin\/roles/,
    module: "roles",
    action: "view",
    anyOf: ["roles", "permission"],
  },

  { match: /^\/admin\/mailgun/, module: null, action: "view" },
]

/**
 * Resolve required permission for a pathname.
 * @param {string} pathname
 * @returns {{ module: string|null, action: string, anyOf?: string[], allOf?: string[] }|null}
 */
export const resolveAdminRoutePermission = pathname => {
  const path =
    String(pathname || "")
      .split("?")[0]
      .replace(/\/+$/, "") || "/admin"

  const rule = ADMIN_ROUTE_PERMISSION_RULES.find(entry =>
    entry.match.test(path),
  )
  if (!rule) return null

  return {
    module: rule.module,
    action: rule.action || "view",
    anyOf: rule.anyOf,
    allOf: rule.allOf,
  }
}
